import PropTypes from "prop-types";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  CssBaseline,
  GlobalStyles,
} from "@mui/material";
import {
  ThemeProvider as MUIThemeProvider,
  createTheme,
  StyledEngineProvider,
} from "@mui/material/styles";
import { ChakraProvider, extendTheme } from "@chakra-ui/react";
import lightPalette from "./palette";
import typography from "./typography";

const STORAGE_KEY = "digilocker_color_mode";
const ColorModeContext = createContext({
  mode: "light",
  toggleColorMode: () => {},
});

const darkPalette = {
  ...lightPalette,
  mode: "dark",
  primary: {
    ...lightPalette.primary,
    light: "#B38BC7",
    main: "#9463AD",
    dark: "#70418A",
  },
  secondary: {
    ...lightPalette.secondary,
    light: "#79A8CE",
    main: "#4C86B7",
    dark: "#2F6FA5",
  },
  success: {
    ...lightPalette.success,
    light: "#69D8C1",
    main: "#36C5A4",
    dark: "#18A88C",
    contrastText: "#061F1B",
  },
  info: {
    ...lightPalette.info,
    light: "#7dd3fc",
    main: "#38bdf8",
    dark: "#bae6fd",
  },
  warning: {
    ...lightPalette.warning,
    light: "#F7C966",
    main: "#E8A62C",
    dark: "#C78312",
  },
  error: {
    ...lightPalette.error,
    light: "#F09AAA",
    main: "#E47789",
    dark: "#C95368",
  },
  text: {
    primary: "#F2F5FA",
    secondary: "#C7D1DF",
    disabled: "#74839A",
  },
  divider: "rgba(139, 161, 190, 0.24)",
  background: {
    paper: "#151D30",
    default: "#0E1422",
    neutral: "#202C42",
  },
  action: {
    ...lightPalette.action,
    active: "#cbd5e1",
    hover: "rgba(148, 163, 184, 0.08)",
    selected: "rgba(148, 99, 173, 0.2)",
    disabled: "rgba(148, 163, 184, 0.48)",
    disabledBackground: "rgba(148, 163, 184, 0.12)",
    focus: "rgba(148, 99, 173, 0.28)",
  },
};

const componentOverrides = {
  MuiCssBaseline: {
    styleOverrides: {
      html: { minHeight: "100%", colorScheme: "light" },
      body: {
        minHeight: "100%",
        transition: "background-color .2s ease, color .2s ease",
      },
      "#root": { minHeight: "100vh" },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundImage: "none",
        borderColor: theme.palette.divider,
        transition:
          "background-color .2s ease, border-color .2s ease, box-shadow .2s ease, transform .2s ease",
      }),
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        minHeight: 38,
        borderRadius: 10,
        fontWeight: 750,
        textTransform: "none",
        letterSpacing: "-0.01em",
        transition:
          "background-color .18s ease, border-color .18s ease, color .18s ease, transform .18s ease, box-shadow .18s ease",
        "&:active": { transform: "translateY(1px)" },
      },
      containedPrimary: ({ theme }) => ({
        color: "#fff",
        background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main} 52%, ${theme.palette.primary.dark})`,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 8px 22px rgba(82, 35, 119, .3)"
            : "0 8px 20px rgba(82, 35, 119, .2)",
        "&:hover": {
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          boxShadow: "0 10px 26px rgba(82, 35, 119, .28)",
        },
      }),
      outlined: ({ theme }) => ({
        borderColor: theme.palette.divider,
        "&:hover": {
          borderColor: theme.palette.primary.main,
          backgroundColor:
            theme.palette.mode === "dark" ? "#241544" : "#eee7f3",
        },
      }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        transition:
          "background-color .18s ease, color .18s ease, transform .18s ease",
        "&:active": { transform: "scale(.96)" },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        fontWeight: 700,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({ borderColor: theme.palette.divider }),
      head: ({ theme }) => ({
        color: theme.palette.text.secondary,
        backgroundColor:
          theme.palette.mode === "dark" ? "#1B273C" : "#F1F4F8",
      }),
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: ({ theme }) => ({
        transition: "background-color .16s ease",
        "&.MuiTableRow-hover:hover": {
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(76, 134, 183, .12)"
              : "rgba(47, 111, 165, .04)",
        },
      }),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor:
          theme.palette.mode === "dark" ? "rgba(21, 29, 48, .88)" : "#fff",
      }),
      notchedOutline: ({ theme }) => ({ borderColor: theme.palette.divider }),
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        "&.Mui-focused": { color: theme.palette.primary.main },
      }),
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        margin: "2px 6px",
        borderRadius: 8,
        "&.Mui-selected": {
          backgroundColor:
            theme.palette.mode === "dark" ? "#241544" : "#eee7f3",
        },
      }),
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 16,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 28px 80px rgba(0, 0, 20, .55)"
            : "0 28px 80px rgba(8, 11, 56, .16)",
      }),
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
      }),
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: ({ theme }) => ({
        backgroundColor: theme.palette.mode === "dark" ? "#e2e8f0" : "#172033",
        color: theme.palette.mode === "dark" ? "#0f172a" : "#fff",
      }),
    },
  },
  MuiSkeleton: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(76, 134, 183, .2)"
              : "rgba(47, 111, 165, .08)",
      }),
    },
  },
};

const darkGlobalStyles = {
  ":root": {
    "--dl-bg": "#f4f6fa",
    "--dl-surface": "#ffffff",
    "--dl-surface-soft": "#f8f9fc",
    "--dl-surface-muted": "#edf1f6",
    "--dl-text": "#080b38",
    "--dl-text-secondary": "#133155",
    "--dl-text-muted": "#536b83",
    "--dl-text-faint": "#7b899b",
    "--dl-border": "rgba(19, 49, 85, .16)",
    "--dl-border-soft": "rgba(19, 49, 85, .09)",
    "--dl-shadow": "rgba(8, 11, 56, .08)",
    "--dl-purple": "#522377",
    "--dl-purple-deep": "#36195b",
    "--dl-navy": "#080b38",
    "--dl-blue-deep": "#133155",
    "--dl-blue": "#2f6fa5",
    "--dl-teal": "#087765",
    "--dl-amber": "#a15d05",
    "--dl-coral": "#b63d54",
    "--dl-primary-soft": "#f1eaf5",
    "--dl-success-soft": "#e7f7f3",
    "--dl-warning-soft": "#fff4dd",
    "--dl-danger-soft": "#fdecef",
    "--dl-info-soft": "#eaf3fa",
  },
  "html[data-color-mode='dark']": {
    colorScheme: "dark",
    "--dl-bg": "#0e1422",
    "--dl-surface": "#151d30",
    "--dl-surface-soft": "#1b273c",
    "--dl-surface-muted": "#202c42",
    "--dl-text": "#f2f5fa",
    "--dl-text-secondary": "#c7d1df",
    "--dl-text-muted": "#9aa8bb",
    "--dl-text-faint": "#74839a",
    "--dl-border": "rgba(139, 161, 190, .24)",
    "--dl-border-soft": "rgba(139, 161, 190, .13)",
    "--dl-shadow": "rgba(1, 5, 14, .36)",
    "--dl-purple": "#c3a0d2",
    "--dl-purple-deep": "#36195b",
    "--dl-navy": "#0e1422",
    "--dl-blue-deep": "#133155",
    "--dl-blue": "#79a8ce",
    "--dl-teal": "#69d8c1",
    "--dl-amber": "#f7c966",
    "--dl-coral": "#f09aaa",
    "--dl-primary-soft": "#2c2038",
    "--dl-success-soft": "#17342f",
    "--dl-warning-soft": "#392e1c",
    "--dl-danger-soft": "#3a222b",
    "--dl-info-soft": "#1b3045",
  },
  "html[data-color-mode='dark'] body": {
    backgroundColor: "#0e1422",
    color: "#f2f5fa",
  },
  "html[data-color-mode='dark'] main, html[data-color-mode='dark'] .admin-main":
    {
      backgroundColor: "#0e1422",
      color: "#f2f5fa",
    },
  "html[data-color-mode='dark'] .MuiAppBar-root.header": {
    background: "rgba(14, 20, 34, .96) !important",
    borderColor: "rgba(139, 161, 190, .2) !important",
    color: "#f2f5fa !important",
  },
  "html[data-color-mode='dark'] .MuiDrawer-paper": {
    background: "linear-gradient(180deg, #151d30 0%, #101725 100%) !important",
    borderColor: "rgba(139, 161, 190, .2) !important",
    color: "#f2f5fa",
  },
  "html[data-color-mode='dark'] .MuiPaper-root": {
    borderColor: "rgba(139, 161, 190, .24) !important",
  },
  "html[data-color-mode='dark'] .MuiTableContainer-root": {
    backgroundColor: "#151d30 !important",
  },
  "html[data-color-mode='dark'] .MuiTableCell-root": {
    borderColor: "rgba(139, 161, 190, .18) !important",
  },
  "html[data-color-mode='dark'] .MuiTableHead-root .MuiTableCell-root": {
    backgroundColor: "#1b273c !important",
    color: "#c7d1df !important",
  },
  "html[data-color-mode='dark'] .MuiTableBody-root .MuiTableRow-root:hover": {
    backgroundColor: "rgba(76, 134, 183, .1) !important",
  },
  "html[data-color-mode='dark'] .MuiInputBase-root, html[data-color-mode='dark'] .MuiSelect-select":
    {
    color: "#f2f5fa",
  },
  "html[data-color-mode='dark'] .MuiInputLabel-root": { color: "#94a3b8" },
  "html[data-color-mode='dark'] .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(139, 161, 190, .32) !important",
  },
  "html[data-color-mode='dark'] .MuiDivider-root": {
    borderColor: "rgba(139, 161, 190, .2)",
  },
  "html[data-color-mode='dark'] .MuiDialog-paper, html[data-color-mode='dark'] .MuiPopover-paper":
    {
      backgroundColor: "#151d30 !important",
      color: "#f2f5fa",
  },
  "html[data-color-mode='dark'] input, html[data-color-mode='dark'] textarea": {
    caretColor: "#69d8c1",
  },
  "html[data-color-mode='dark'] ::-webkit-scrollbar-thumb": {
    backgroundColor: "#415873",
    border: "3px solid #0e1422",
    borderRadius: 10,
  },
  "*::-webkit-scrollbar": {
    width: 10,
    height: 10,
  },
  "*::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(37, 77, 112, .42)",
    border: "3px solid transparent",
    backgroundClip: "padding-box",
    borderRadius: 10,
  },
  "::selection": {
    color: "#fff",
    backgroundColor: "#522377",
  },
  ":focus-visible": {
    outline: "2px solid #7a4a9a",
    outlineOffset: 2,
  },
  "html[data-color-mode='dark'] .apexcharts-tooltip, html[data-color-mode='dark'] .apexcharts-xaxistooltip":
    {
    background: "#202c42 !important",
    borderColor: "#415873 !important",
    color: "#f2f5fa !important",
      boxShadow: "0 10px 28px rgba(0, 0, 0, .28) !important",
    },
  "html[data-color-mode='dark'] .apexcharts-tooltip-title": {
    background: "#27364e !important",
    borderColor: "#415873 !important",
  },
  "@media (prefers-reduced-motion: reduce)": {
    "*, *::before, *::after": {
      transitionDuration: "0.01ms !important",
    },
  },
};

ThemeProvider.propTypes = {
  children: PropTypes.node,
};

export const useAppColorMode = () => useContext(ColorModeContext);

export default function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "dark" || saved === "light") return saved;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  });

  useEffect(() => {
    document.documentElement.dataset.colorMode = mode;
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const toggleColorMode = useCallback(() => {
    setMode((current) => (current === "dark" ? "light" : "dark"));
  }, []);

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: mode === "dark" ? darkPalette : { ...lightPalette, mode: "light" },
        typography,
        components: componentOverrides,
      }),
    [mode]
  );

  const chakraTheme = useMemo(
    () =>
      extendTheme({
        config: { initialColorMode: mode, useSystemColorMode: false },
        styles: {
          global: {
            body: {
              bg: mode === "dark" ? "#0e1422" : "#f4f6fa",
              color: mode === "dark" ? "#f2f5fa" : "#080b38",
            },
          },
        },
      }),
    [mode]
  );

  const contextValue = useMemo(
    () => ({ mode, toggleColorMode }),
    [mode, toggleColorMode]
  );

  return (
    <ColorModeContext.Provider value={contextValue}>
      <ChakraProvider theme={chakraTheme} resetCSS={false}>
        <StyledEngineProvider injectFirst>
          <MUIThemeProvider theme={muiTheme}>
            <CssBaseline />
            <GlobalStyles styles={darkGlobalStyles} />
            {children}
          </MUIThemeProvider>
        </StyledEngineProvider>
      </ChakraProvider>
    </ColorModeContext.Provider>
  );
}
